import {
    AuthenticationResult,
    PublicClientApplication,
} from "@azure/msal-browser";
import type { Configuration } from "@azure/msal-browser";
import { AccountInfo, ScopeSet } from "@azure/msal-common";

export class MsalLayer {
    private msalInstance: PublicClientApplication;

    public constructor(config: Configuration) {
        this.msalInstance = new PublicClientApplication(config);
    }

    public get instance(): PublicClientApplication {
        return this.msalInstance;
    }

    public async loginPopup(
        scopes: ScopeSet = new ScopeSet([
            "openid",
            "profile",
            "email",
        ]),
        popupConfig?: any // Adjusted type to 'any' or a more appropriate type if known
    ): Promise<AuthenticationResult> {
        return await this.msalInstance.loginPopup({
            scopes: scopes.asArray(),
            popupWindowAttributes: popupConfig,
            prompt: "login",
        });
    }

    public async loginRedirect(
        scopes: ScopeSet = new ScopeSet([
            "openid",
            "profile",
            "email",
        ])
    ): Promise<void> {
        // loginRedirect navigates away, so nothing after it runs. The startup
        // handleRedirectPromise() (in main.ts) handles the return leg.
        await this.msalInstance.loginRedirect({
            scopes: scopes.asArray(),
            prompt: "login",
        });
    }

    public async logout(): Promise<void> {
        return await this.msalInstance.logoutPopup();
    }

    public async logoutRedirect(postLogoutRedirectUri?: string): Promise<void> {
        // logoutRedirect navigates the whole tab to the logout endpoint and back, so nothing after
        // it runs. The startup handleRedirectPromise() (in main.ts) handles the return leg, which
        // re-runs app initialization. Mirrors loginRedirect above.
        await this.msalInstance.logoutRedirect({ postLogoutRedirectUri });
    }

    /** Clears the cached account and tokens but never navigates: returning false from
     *  onRedirectNavigate stops MSAL before it leaves for the logout endpoint.
     *
     *  logoutRedirect is the only public way to clear MSAL's cache, but its preflight throws
     *  redirect_in_iframe inside an iframe unless allowRedirectInIframe is set, and this runs
     *  precisely when the app is embedded. Nothing navigates here, so the guard is lifted for this
     *  one call. The preflight runs synchronously, so it is restored before the first await. */
    public async clearLocalSession(): Promise<void> {
        // getConfiguration returns MSAL's live config object, so this toggles the check it reads.
        const system = this.msalInstance.getConfiguration().system;
        const allowRedirectInIframe = system.allowRedirectInIframe;
        system.allowRedirectInIframe = true;
        let logout: Promise<void>;
        try {
            logout = this.msalInstance.logoutRedirect({ onRedirectNavigate: () => false });
        } finally {
            system.allowRedirectInIframe = allowRedirectInIframe;
        }
        await logout;
    }

    public user(): AccountInfo | undefined {
        const currentAccounts = this.msalInstance.getAllAccounts();
        // no user has signed in
        if (!currentAccounts || currentAccounts.length === 0) return undefined;

        // improvements in future could be to handle this a bit cleaner... but for now it's fine.
        return currentAccounts[0];
    }

    public async acquireToken(
        scopes: ScopeSet = new ScopeSet(["openid"])
    ): Promise<AuthenticationResult> {
        let resp: AuthenticationResult;

        try {
            resp = await this.msalInstance.acquireTokenSilent({
                scopes: scopes.asArray(),
                account: this.user(),
            });
        } catch (err) {
            // silent sometimes fails, so let's just try popup
            resp = await this.msalInstance.acquireTokenPopup({
                scopes: scopes.asArray(),
                account: this.user(),
            });
        }

        return resp;
    }

    public isAuthenticated(): boolean {
        const accounts = this.msalInstance.getAllAccounts();
        return accounts && accounts.length > 0;
    }
}
